import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface IToast {
    id: number;
    message: string;
    type: ToastType;
}

interface IToastContext {
    showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<IToastContext | undefined>(undefined);

const slideIn = keyframes`
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
`;

const ToastContainer = styled.div`
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ToastItem = styled.div<{ type: ToastType }>`
    padding: 15px 25px;
    border-radius: 8px;
    color: white;
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    animation: ${slideIn} 0.3s ease-out;
    background-color: ${props => {
        switch (props.type) {
            case 'success': return '#2ecc71';
            case 'error': return '#e74c3c';
            case 'warning': return '#f1c40f';
            case 'info': return '#3498db';
            default: return '#333';
        }
    }};
`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<IToast[]>([]);

    const showToast = useCallback((message: string, type: ToastType) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <ToastContainer>
                {toasts.map(toast => (
                    <ToastItem key={toast.id} type={toast.type}>
                        {toast.message}
                    </ToastItem>
                ))}
            </ToastContainer>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
